import { useLearnEnabled } from '@/lib/learnFeature';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import JoinNowModal from '@/components/JoinNowModal';
import CollaboratorJoinModal from '@/components/CollaboratorJoinModal';
import UserAccountJoinModal from '@/components/UserAccountJoinModal';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import { useBuyerAuth } from '@/context/BuyerAuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';
import { API_BASE_URL as API } from '@/lib/apiBase';

const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || 'https://www.kalamanch.art';
const MARKET_SITE_URL = process.env.REACT_APP_MARKET_SITE_URL || 'https://market.kalamanch.art';
const LEARN_SITE_URL = process.env.REACT_APP_LEARN_SITE_URL || 'https://learn.kalamanch.art';

const Navbar = () => {
  const learnEnabled = useLearnEnabled();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [userModalIntent, setUserModalIntent] = useState(null);
  const [showJoinChooser, setShowJoinChooser] = useState(false);
  const [showLoginChooser, setShowLoginChooser] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [learnerAccount, setLearnerAccount] = useState(null);
  const location = useLocation();
  const { isAuthenticated: artistAuthenticated, logout: artistLogout } = useArtistAuth();
  const { isAuthenticated: collaboratorAuthenticated, logout: collaboratorLogout } = useCollaboratorAuth();
  const { isAuthenticated: buyerAuthenticated, logoutBuyer, refreshBuyer } = useBuyerAuth();
  const { isHindi, toggleLanguage } = useLanguage();
  const isCollaboratorRoute = location.pathname.startsWith('/collaborator');
  const isMarketplaceHost = typeof window !== 'undefined' && window.location.hostname === 'market.kalamanch.art';
  const isLearnHost = typeof window !== 'undefined' && window.location.hostname === 'learn.kalamanch.art';
  const isExternalPath = (path) => /^https?:\/\//.test(path);
  const mainSitePath = (path = '/') => (isMarketplaceHost || isLearnHost ? `${MAIN_SITE_URL}${path}` : path);
  const marketplacePath =
    isMarketplaceHost
      ? '/'
      : MARKET_SITE_URL;
  const learnPath =
    isLearnHost
      ? '/'
      : LEARN_SITE_URL;
  const learnAccountPath =
    isLearnHost
      ? '/?section=my-plans'
      : `${LEARN_SITE_URL}/?section=my-plans`;
  const activeRole = collaboratorAuthenticated && (isCollaboratorRoute || !artistAuthenticated)
    ? 'collaborator'
    : artistAuthenticated
      ? 'artist'
      : buyerAuthenticated && isMarketplaceHost
        ? 'buyer'
        : learnerAccount
          ? 'learner'
          : null;

  const refreshLearnerAccount = async () => {
    if (!learnEnabled) { setLearnerAccount(null); return; }
    try {
      const response = await axios.get(`${API}/learn/me`, { withCredentials: true });
      const account = response.data || null;
      const roles = Array.isArray(account?.roles) ? account.roles : [];
      setLearnerAccount(roles.includes('learner') || account?.role === 'learner' ? account : null);
    } catch (_ignored) {
      setLearnerAccount(null);
    }
  };

  const learnerLogout = async () => {
    try {
      await axios.post(`${API}/learn/logout`, {}, { withCredentials: true });
    } catch (_ignored) {
      // Clear the visible state even if the server is temporarily unavailable.
    }
    setLearnerAccount(null);
    window.dispatchEvent(new CustomEvent('learnAuthChanged', { detail: { authenticated: false } }));
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const accountUiOpen = Boolean(
      isMobileMenuOpen || showArtistModal || showCollaboratorModal || userModalIntent || showJoinChooser || showLoginChooser
    );
    window.dispatchEvent(new CustomEvent('kalamanchAccountUiChanged', { detail: { open: accountUiOpen } }));
    return () => window.dispatchEvent(new CustomEvent('kalamanchAccountUiChanged', { detail: { open: false } }));
  }, [isMobileMenuOpen, showArtistModal, showCollaboratorModal, userModalIntent, showJoinChooser, showLoginChooser]);

  useEffect(() => {
    refreshLearnerAccount();
    refreshBuyer();
    const handleLearnerAuthChanged = () => {
      refreshLearnerAccount();
      refreshBuyer();
    };
    window.addEventListener('learnAuthChanged', handleLearnerAuthChanged);
    return () => window.removeEventListener('learnAuthChanged', handleLearnerAuthChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnEnabled]);

  useEffect(() => {
    const openArtistJoin = () => setShowArtistModal(true);
    const openCollaboratorJoin = () => setShowCollaboratorModal(true);
    const openBuyerLogin = () => window.location.assign(`${MARKET_SITE_URL}/marketplace/account`);
    const openLearnerLogin = () => setUserModalIntent('learner');
    window.addEventListener('openArtistJoin', openArtistJoin);
    window.addEventListener('openCollaboratorJoin', openCollaboratorJoin);
    window.addEventListener('openBuyerLogin', openBuyerLogin);
    window.addEventListener('openLearnerLogin', openLearnerLogin);
    return () => {
      window.removeEventListener('openArtistJoin', openArtistJoin);
      window.removeEventListener('openCollaboratorJoin', openCollaboratorJoin);
      window.removeEventListener('openBuyerLogin', openBuyerLogin);
      window.removeEventListener('openLearnerLogin', openLearnerLogin);
    };
  }, []);

  const primaryNavLinks = [
    { name: 'Home', hindi: 'होम', path: mainSitePath('/') },
    { name: 'Artists', hindi: 'कलाकार', path: mainSitePath('/artists') },
    { name: 'Collaborators', hindi: 'सहयोगी', path: mainSitePath('/collaborators') },
    { name: 'Competitions', hindi: 'प्रतियोगिताएं', path: mainSitePath('/competitions') },
    { name: 'Auditions', hindi: 'ऑडिशन', path: mainSitePath('/live-auditions') },
    { name: 'Projects', hindi: 'प्रोजेक्ट्स', path: mainSitePath('/projects') },
    ...(learnEnabled ? [{ name: 'Learn', hindi: 'सीखें', path: learnPath }] : []),
    { name: 'AI Studio', hindi: 'AI स्टूडियो', path: mainSitePath('/ai-studio') },
  ];
  const moreNavLinks = [
    { name: 'Indian Art', hindi: 'भारतीय कला', path: mainSitePath('/indian-art') },
    { name: 'Art Exhibition', hindi: 'कला प्रदर्शनी', path: mainSitePath('/art-exhibition') },
    { name: 'Marketplace', hindi: 'मार्केटप्लेस', path: marketplacePath },
    { name: 'AI Mentor Pro', hindi: 'AI मेंटर प्रो', path: mainSitePath('/ai-mentor') },
    { name: 'AI Manager', hindi: 'AI मैनेजर', path: mainSitePath('/ai-manager') },
    { name: 'Collab Finder', hindi: 'सहयोग खोजें', path: mainSitePath('/collab-match') },
    { name: 'About', hindi: 'परिचय', path: mainSitePath('/about') },
    { name: 'Contact', hindi: 'संपर्क', path: mainSitePath('/contact') },
  ];
  const navLinks = [...primaryNavLinks, ...moreNavLinks];

  const openJoinChooser = (device) => {
    trackEvent('navbar_join_free_click', { device });
    if (typeof window !== 'undefined' && window.location.hostname === 'learn.kalamanch.art') {
      setUserModalIntent('learner');
      return;
    }
    setShowJoinChooser(true);
  };

  const openLoginChooser = (device) => {
    trackEvent('navbar_login_click', { device });
    setShowLoginChooser(true);
  };

  if (isMarketplaceHost || location.pathname.startsWith('/marketplace')) {
    return (
      <header className="fixed inset-x-0 top-0 z-50 border-b border-purple-100 bg-white shadow-sm">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4" aria-label="Marketplace navigation">
          <Link to="/marketplace" className="text-lg font-black text-purple-900">KalaManch Market</Link>
          <div className="flex items-center gap-4 text-sm font-bold">
            <Link to="/marketplace">Browse</Link>
            <Link to="/marketplace/account" className="rounded-full bg-purple-900 px-4 py-2 text-white">Customer account</Link>
          </div>
        </nav>
      </header>
    );
  }

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-slate-200/90 bg-white/95 backdrop-blur ${isScrolled ? 'shadow-lg py-2 sm:py-2.5' : 'shadow-sm py-2.5 sm:py-3'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-4">
            {isExternalPath(mainSitePath('/')) ? (
              <a href={mainSitePath('/')} className="flex min-w-0 items-center justify-self-start shrink-0 rounded-[1.35rem] bg-white/90 px-3 py-2 shadow-md ring-1 ring-orange-100 sm:rounded-[1.7rem] sm:px-5 sm:py-2.5">
                <img
                  src="/images/kalamanch-logo.png"
                  alt="Kala Manch Art"
                  className={`w-auto max-w-[8rem] object-contain transition-all duration-300 sm:max-w-[9.5rem] ${isScrolled ? 'h-10 sm:h-12 md:h-[3.2rem]' : 'h-11 sm:h-[3.2rem] md:h-[3.55rem]'}`}
                />
              </a>
            ) : (
            <Link to={mainSitePath('/')} className="flex min-w-0 items-center justify-self-start shrink-0 rounded-[1.35rem] bg-white/90 px-3 py-2 shadow-md ring-1 ring-orange-100 sm:rounded-[1.7rem] sm:px-5 sm:py-2.5">
              <img
                src="/images/kalamanch-logo.png"
                alt="Kala Manch Art"
                className={`w-auto max-w-[8rem] object-contain transition-all duration-300 sm:max-w-[9.5rem] ${isScrolled ? 'h-10 sm:h-12 md:h-[3.2rem]' : 'h-11 sm:h-[3.2rem] md:h-[3.55rem]'}`}
              />
            </Link>
            )}

            <div className="hidden xl:flex min-w-0 flex-1 items-center justify-center gap-3 whitespace-nowrap">
              {primaryNavLinks.map((link) => (
                isExternalPath(link.path) ? (
                  <a
                    key={`${link.name}-${link.path}`}
                    href={link.path}
                    className="text-[13px] font-semibold text-slate-700 transition-colors duration-200 hover:text-[#FF6F00] whitespace-nowrap"
                  >
                    {isHindi ? link.hindi : link.name}
                  </a>
                ) : (
                  <Link
                    key={`${link.name}-${link.path}`}
                    to={link.path}
                    className={`text-[13px] font-semibold transition-colors duration-200 whitespace-nowrap ${location.pathname === link.path ? 'text-[#C45A00]' : 'text-slate-700 hover:text-[#FF6F00]'}`}
                  >
                    {isHindi ? link.hindi : link.name}
                  </Link>
                )
              ))}
              <div
                className="relative"
                onMouseEnter={() => setShowMoreMenu(true)}
                onMouseLeave={() => setShowMoreMenu(false)}
              >
                <button
                  type="button"
                  onClick={() => setShowMoreMenu((value) => !value)}
                  onFocus={() => setShowMoreMenu(true)}
                  aria-expanded={showMoreMenu}
                  aria-haspopup="menu"
                  className="whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-[#FF6F00]"
                >
                  More
                </button>
                {showMoreMenu && (
                  <div className="absolute right-0 top-full z-[80] w-48 pt-2">
                    <div className="rounded-2xl border border-orange-100 bg-white p-2 shadow-xl" role="menu">
                      {moreNavLinks.map((link) => (
                        link.path.startsWith('http') ? (
                          <a
                            key={`${link.name}-${link.path}`}
                            href={link.path}
                            onClick={() => setShowMoreMenu(false)}
                            className="block rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-[#FF6F00]"
                            role="menuitem"
                          >
                            {isHindi ? link.hindi : link.name}
                          </a>
                        ) : (
                          <Link
                            key={`${link.name}-${link.path}`}
                            to={link.path}
                            onClick={() => setShowMoreMenu(false)}
                            className={`block rounded-xl px-3 py-2 text-sm font-semibold transition ${location.pathname === link.path ? 'bg-orange-50 text-[#C45A00]' : 'text-slate-700 hover:bg-orange-50 hover:text-[#FF6F00]'}`}
                            role="menuitem"
                          >
                            {isHindi ? link.hindi : link.name}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden xl:flex shrink-0 items-center justify-end gap-2">
              <button
                type="button"
                onClick={toggleLanguage}
                className="inline-flex h-11 min-w-14 items-center justify-center rounded-full border border-orange-200 bg-white/80 px-3 text-sm font-extrabold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50"
                aria-label="Switch language"
                aria-pressed={isHindi}
              >
                {isHindi ? 'English' : 'Hindi'}
              </button>
              {activeRole === 'artist' ? (
                <div className="flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-2">
                  <Link to="/my-account" className="text-sm font-semibold text-[#B45309] hover:text-[#FF6F00] whitespace-nowrap">My Account</Link>
                  <span className="text-orange-200">|</span>
                  <button type="button" className="text-sm font-semibold text-[#FF6F00] whitespace-nowrap" onClick={artistLogout}>Logout</button>
                </div>
              ) : activeRole === 'collaborator' ? (
                <div className="flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-2">
                  <Link to="/collaborator/account" className="text-sm font-semibold text-[#0F766E] hover:text-[#00BFA5] whitespace-nowrap">My Account</Link>
                  <span className="text-teal-200">|</span>
                  <button type="button" className="text-sm font-semibold text-[#00BFA5] whitespace-nowrap" onClick={collaboratorLogout}>Logout</button>
                </div>
              ) : activeRole === 'buyer' ? (
                <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2">
                  <Link to={marketplacePath} className="text-sm font-semibold text-sky-700 hover:text-sky-600 whitespace-nowrap">My Account</Link>
                  <span className="text-sky-200">|</span>
                  <button type="button" className="text-sm font-semibold text-sky-700 whitespace-nowrap" onClick={logoutBuyer}>Logout</button>
                </div>
              ) : learnEnabled && activeRole === 'learner' ? (
                <div className="flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-2">
                  <Link to={learnAccountPath} className="text-sm font-semibold text-purple-700 hover:text-purple-600 whitespace-nowrap">My Learn</Link>
                  <span className="text-purple-200">|</span>
                  <button type="button" className="text-sm font-semibold text-purple-700 whitespace-nowrap" onClick={learnerLogout}>Logout</button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="inline-flex h-11 min-w-20 items-center justify-center rounded-full border border-orange-200 bg-white/80 px-4 text-sm font-extrabold text-[#C45A00] shadow-sm whitespace-nowrap transition hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50"
                    onClick={() => openLoginChooser('desktop')}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-11 min-w-28 items-center justify-center rounded-full border border-[#5B21B6] bg-[#5B21B6] px-5 text-sm font-extrabold text-white shadow-[0_14px_30px_rgba(91,33,182,0.24)] whitespace-nowrap transition hover:-translate-y-0.5 hover:bg-[#4C1D95] hover:shadow-[0_18px_36px_rgba(91,33,182,0.3)]"
                    onClick={() => openJoinChooser('desktop')}
                  >
                    Join Free
                  </button>
                </>
              )}
            </div>

            <button className="ml-auto inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-orange-100 bg-white/70 p-2 text-gray-800 focus:outline-none xl:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle navigation menu">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="xl:hidden mt-3 pb-3 max-h-[calc(100dvh-6rem)] overflow-y-auto">
              {navLinks.map((link) => (
                link.path.startsWith('http') ? (
                  <a key={`${link.name}-${link.path}`} href={link.path} className="block py-3 text-base font-medium text-gray-700 hover:text-[#FF6F00]" onClick={() => setIsMobileMenuOpen(false)}>
                    {isHindi ? link.hindi : link.name}
                  </a>
                ) : (
                  <Link key={`${link.name}-${link.path}`} to={link.path} className="block py-3 text-base font-medium text-gray-700 hover:text-[#FF6F00]" onClick={() => setIsMobileMenuOpen(false)}>
                    {isHindi ? link.hindi : link.name}
                  </Link>
                )
              ))}
              <button
                type="button"
                onClick={toggleLanguage}
                className="mt-2 w-full rounded-full border border-orange-200 bg-white px-4 py-2 text-sm font-bold text-slate-700"
                aria-pressed={isHindi}
              >
                {isHindi ? 'Switch to English' : 'View in Hindi'}
              </button>
              {activeRole === 'artist' ? (
                <>
                  <Link to="/my-account" className="block py-3 text-base font-medium text-gray-700 hover:text-[#FF6F00]" onClick={() => setIsMobileMenuOpen(false)}>My Account</Link>
                  <button className="btn-outline w-full mt-3" onClick={() => { artistLogout(); setIsMobileMenuOpen(false); }}>Artist Logout</button>
                </>
              ) : activeRole === 'collaborator' ? (
                <>
                  <Link to="/collaborator/account" className="block py-3 text-base font-medium text-gray-700 hover:text-[#00BFA5]" onClick={() => setIsMobileMenuOpen(false)}>My Account</Link>
                  <button className="btn-primary w-full mt-3" onClick={() => { collaboratorLogout(); setIsMobileMenuOpen(false); }}>Collaborator Logout</button>
                </>
              ) : activeRole === 'buyer' ? (
                <>
                  <Link to={marketplacePath} className="block py-3 text-base font-medium text-gray-700 hover:text-sky-600" onClick={() => setIsMobileMenuOpen(false)}>My Account</Link>
                  <button className="btn-primary w-full mt-3" onClick={() => { logoutBuyer(); setIsMobileMenuOpen(false); }}>Buyer Logout</button>
                </>
              ) : learnEnabled && activeRole === 'learner' ? (
                <>
                  <Link to={learnAccountPath} className="block py-3 text-base font-medium text-gray-700 hover:text-purple-600" onClick={() => setIsMobileMenuOpen(false)}>My Learn</Link>
                  <button className="btn-primary w-full mt-3" onClick={() => { learnerLogout(); setIsMobileMenuOpen(false); }}>Learner Logout</button>
                </>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="rounded-full border border-orange-200 bg-white px-4 py-3 text-base font-extrabold text-[#C45A00] shadow-sm transition hover:bg-orange-50"
                    onClick={() => {
                      openLoginChooser('mobile');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-[#5B21B6] bg-[#5B21B6] px-4 py-3 text-base font-extrabold text-white shadow-[0_12px_24px_rgba(91,33,182,0.22)] transition hover:bg-[#4C1D95]"
                    onClick={() => {
                      openJoinChooser('mobile');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    Join Free
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {showJoinChooser && !activeRole && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/55 px-3 py-4 sm:items-center sm:px-4" onClick={() => setShowJoinChooser(false)}>
          <div className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl sm:p-6" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#C45A00]">Join KalaManch</p>
                <h3 className="mt-2 text-2xl font-bold text-slate-900">Choose how you want to join</h3>
                <p className="mt-2 text-sm text-slate-600">Select your path and continue with the right KalaManch account flow.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowJoinChooser(false)}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4 text-left transition hover:border-orange-300 hover:bg-orange-100"
                onClick={() => {
                  setShowJoinChooser(false);
                  trackEvent('join_free_choose_artist', { source: 'navbar' });
                  setShowArtistModal(true);
                }}
              >
                <div className="text-base font-bold text-slate-900">Join as Artist</div>
                <div className="mt-1 text-sm text-slate-600">Create your profile, portfolio, and availability.</div>
              </button>

              <button
                type="button"
                className="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-4 text-left transition hover:border-teal-300 hover:bg-teal-100"
                onClick={() => {
                  setShowJoinChooser(false);
                  trackEvent('join_free_choose_collaborator', { source: 'navbar' });
                  setShowCollaboratorModal(true);
                }}
              >
                <div className="text-base font-bold text-slate-900">Join as Collaborator</div>
                <div className="mt-1 text-sm text-slate-600">Send enquiries, discover talent, and manage conversations.</div>
              </button>
              {learnEnabled && (<button
                type="button"
                className="rounded-2xl border border-purple-200 bg-purple-50 px-4 py-4 text-left transition hover:border-purple-300 hover:bg-purple-100"
                onClick={() => {
                  setShowJoinChooser(false);
                  trackEvent('join_free_choose_learner', { source: 'navbar' });
                  setUserModalIntent('learner');
                }}
              >
                <div className="text-base font-bold text-slate-900">Join as Learner</div>
                <div className="mt-1 text-sm text-slate-600">Enroll in Kalamanch Learn lessons with one account.</div>
              </button>)}
              <button
                type="button"
                className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-left transition hover:border-sky-300 hover:bg-sky-100"
                onClick={() => {
                  setShowJoinChooser(false);
                  trackEvent('join_free_choose_buyer', { source: 'navbar' });
                  window.location.assign(`${MARKET_SITE_URL}/marketplace/account`);
                }}
              >
                <div className="text-base font-bold text-slate-900">Marketplace customer</div>
                <div className="mt-1 text-sm text-slate-600">Shop through a separate marketplace customer account.</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoginChooser && !activeRole && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-900/55 px-3 py-4 sm:items-center sm:px-4" onClick={() => setShowLoginChooser(false)}>
          <div className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl sm:p-6" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#C45A00]">Login</p>
                <h3 className="mt-2 text-2xl font-bold text-slate-900">Continue to your account</h3>
                <p className="mt-2 text-sm text-slate-600">Choose the account type you used while joining KalaManch.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowLoginChooser(false)}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4 text-left transition hover:border-orange-300 hover:bg-orange-100"
                onClick={() => {
                  trackEvent('login_choose_artist', { source: 'navbar' });
                  setShowLoginChooser(false);
                  setShowArtistModal(true);
                }}
              >
                <div className="text-base font-bold text-slate-900">Artist Login</div>
                <div className="mt-1 text-sm text-slate-600">Access artist profile, videos, PMB, and AI Studio.</div>
              </button>

              <button
                type="button"
                className="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-4 text-left transition hover:border-teal-300 hover:bg-teal-100"
                onClick={() => {
                  trackEvent('login_choose_collaborator', { source: 'navbar' });
                  setShowLoginChooser(false);
                  setShowCollaboratorModal(true);
                }}
              >
                <div className="text-base font-bold text-slate-900">Collaborator Login</div>
                <div className="mt-1 text-sm text-slate-600">Access enquiries, PMB, artist search, and AI Studio.</div>
              </button>
              {learnEnabled && (<button
                type="button"
                className="rounded-2xl border border-purple-200 bg-purple-50 px-4 py-4 text-left transition hover:border-purple-300 hover:bg-purple-100"
                onClick={() => {
                  trackEvent('login_choose_learner', { source: 'navbar' });
                  setShowLoginChooser(false);
                  setUserModalIntent('learner');
                }}
              >
                <div className="text-base font-bold text-slate-900">Learner Login</div>
                <div className="mt-1 text-sm text-slate-600">Continue lessons and enrolled courses.</div>
              </button>)}
              <button
                type="button"
                className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-left transition hover:border-sky-300 hover:bg-sky-100"
                onClick={() => {
                  trackEvent('login_choose_buyer', { source: 'navbar' });
                  setShowLoginChooser(false);
                  window.location.assign(`${MARKET_SITE_URL}/marketplace/account`);
                }}
              >
                <div className="text-base font-bold text-slate-900">Marketplace customer login</div>
                <div className="mt-1 text-sm text-slate-600">Open the marketplace customer sign-in page.</div>
              </button>
            </div>
          </div>
        </div>
      )}

      <JoinNowModal isOpen={showArtistModal} onClose={() => setShowArtistModal(false)} />
      <CollaboratorJoinModal isOpen={showCollaboratorModal} onClose={() => setShowCollaboratorModal(false)} />
      <UserAccountJoinModal isOpen={Boolean(userModalIntent) && (userModalIntent === 'buyer' || learnEnabled)} intent={userModalIntent || 'learner'} onClose={() => setUserModalIntent(null)} />
    </>
  );
};

export default Navbar;
